import { useState } from 'react';
import { attemptSilentReauth } from '@/lib/auth/reauth';
import { redirectToLogin } from '@/lib/auth/redirectToLogin';
import { cn } from '@/lib/utils';
import { SessionOverlay } from './session-overlay';

interface LoggedOutOverlayProps {
  /** Whether the overlay is visible */
  open: boolean;
  /**
   * Called when the session was restored in place, so the host can dismiss the
   * overlay and refetch. Absent means the overlay can only redirect.
   */
  onRestored?: () => void;
}

/**
 * Full-screen modal overlay shown after the user explicitly logs out.
 * Non-dismissible. No auto-redirect — the user just logged out and bouncing
 * them away without acknowledgement would feel broken.
 */
export function LoggedOutOverlay({ open, onRestored }: LoggedOutOverlayProps) {
  const [busy, setBusy] = useState(false);

  const handleSignIn = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Cheapest outcome first: the control-plane session usually outlives the
      // workspace one, so a session can often be restored without navigating and
      // without losing editor state.
      const result = await attemptSilentReauth();
      if (result === 'ok' && onRestored) {
        onRestored();
        return;
      }
      redirectToLogin('logged-out');
    } finally {
      // Unconditional: the overlay is mounted permanently (see RootLayout), so
      // any outcome that falls through to redirectToLogin — including a
      // misconfigured deploy where it returns false without navigating — must
      // still release the button. Otherwise Sign in silently locks up until a
      // full page reload.
      setBusy(false);
    }
  };

  return (
    <SessionOverlay
      open={open}
      title="You've been signed out"
      description="Your session has ended."
      action={
        <button
          type="button"
          className={cn(
            'inline-flex items-center justify-center',
            'rounded-md px-6 py-2 text-sm font-medium',
            'bg-primary text-primary-foreground',
            'hover:bg-primary/90 transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-offset-2'
          )}
          onClick={handleSignIn}
        >
          Sign in
        </button>
      }
    />
  );
}

export default LoggedOutOverlay;
